import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  inject
} from '@angular/core';

import { I18NextPipe } from 'angular-i18next';
import { Observable, Subject, combineLatest, map, takeUntil, tap } from 'rxjs';

import { LogoutConfig } from '@hdms/shared/data-access';
import {
  HeaderNavigationService,
  HeaderUserService,
  LiveChatProviderService
} from '@hdms/shared/page-frame/data-access';
import {
  MetaMenuType,
  MetaNavItem,
  MetaNavItemsMap,
  PrimaryNavItem,
  UserAccountData
} from '@hdms/shared/page-frame/types';
import { IconsName, ToastMessagesService } from '@hdms/shared/ui-core';
import {
  AnalyticsService,
  BreakpointService,
  EnvironmentService,
  PlatformService,
  StorageService,
  StorageType
} from '@hdms/shared/util-core';

import { STORAGE_KEY_LOGIN_CLICKED } from '../../config/header.const';
import { HeaderConfigService, MinimizeHeaderService } from '../../services';
import { BurgerMenuWrapperComponent } from '../header-burger-menu/burger-menu-wrapper/burger-menu-wrapper.component';
import { HeaderSearchPopoverComponent } from '../header-popover/header-search-popover/header-search-popover.component';

@Component({
  selector: 'header[shrd-pf-ft-page-header]',
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MinimizeHeaderService]
})
export class PageHeaderComponent implements OnInit, OnDestroy {
  @ViewChild(BurgerMenuWrapperComponent) burgerMenuWrapper: BurgerMenuWrapperComponent;
  @ViewChild('searchComponent') searchComponent: HeaderSearchPopoverComponent;
  @ViewChild('primaryNav', { read: ElementRef }) primaryNav: ElementRef;

  @HostBinding('class.shrd-pf-ft-page-header')
  @HostBinding('class.sticky-top')
  private readonly hostClasses = true;

  @HostBinding('class.js-sticky-header')
  @HostBinding('class.is-sticky')
  private readonly legacyClasses: boolean;

  userData: UserAccountData;
  isDesktop = true;
  isMobile = true;
  readonly isMobileApp: boolean;

  readonly iconName = IconsName;
  readonly metaMenuType = MetaMenuType;

  metaNavItemsMap: MetaNavItemsMap = {};
  metaNavItems: MetaNavItem[];
  metaNavItemManageMyTools: MetaNavItem = null;
  primaryNavItems: PrimaryNavItem[] = [];

  readonly tabAccountChanged$: Observable<boolean>;
  readonly logoutConfig$: Observable<LogoutConfig>;
  readonly isCompressed$: Observable<boolean>;

  private focusoutListener: () => void;
  private readonly unsubscribe$ = new Subject<void>();

  private readonly headerNavigationService = inject(HeaderNavigationService);
  private readonly toastMessagesService = inject(ToastMessagesService);
  private readonly i18nextPipe = inject(I18NextPipe);
  private readonly minimizeHeader = inject(MinimizeHeaderService);
  private readonly breakpointService = inject(BreakpointService);
  private readonly headerUserService = inject(HeaderUserService);
  private readonly configService = inject(HeaderConfigService);
  private readonly platform = inject(PlatformService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly liveChatService = inject(LiveChatProviderService);
  private readonly renderer = inject(Renderer2);
  private readonly storageService = inject(StorageService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly envService = inject(EnvironmentService);

  constructor() {
    this.isMobileApp = this.envService.mobileApp;
    this.legacyClasses = !!this.envService.hostContext;

    this.tabAccountChanged$ = this.headerUserService.tabAccountChanged$;
    this.logoutConfig$ = this.configService.logoutConfig$;
    this.isCompressed$ = this.configService.isCompressed$;
  }

  ngOnInit(): void {
    this.subscribeViewSize();
    this.subscribeUserData();
    if (!this.isMobileApp) {
      this.subscribeMetaNavigationData();
      this.subscribePrimaryNavigationData();
    }
    this.liveChatService.checkActiveSession();
    this.minimizeHeader.headerElement = this.element;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.unsubscribeFocusout();
  }

  expandHeader(fullTransition = true): void {
    this.minimizeHeader.toggle(false, fullTransition);
  }

  handleSearchFocus(hasFocus: boolean): void {
    if (this.isDesktop && hasFocus) {
      this.focusoutListener ||= this.renderer.listen(this.element, 'focusout', event =>
        this.handleElementFocusout(event.relatedTarget)
      );
      return;
    }
    this.unsubscribeFocusout();
  }

  private subscribeUserData(): void {
    this.headerUserService
      .getUserData$()
      .pipe(tap(this.checkAccountSync.bind(this)), tap(this.trackUserLoggedIn.bind(this)), takeUntil(this.unsubscribe$))
      .subscribe(userData => {
        this.userData = userData;
        this.cdr.markForCheck();
      });
  }

  private subscribeViewSize(): void {
    // as server doesn't know the screen-width, render both mobile and desktop view,
    this.platform.isBrowser &&
      this.breakpointService.isMobileView$.pipe(takeUntil(this.unsubscribe$)).subscribe(isMobile => {
        this.isDesktop = !isMobile;
        this.isMobile = isMobile;
        this.cdr.detectChanges();
      });
  }

  private subscribePrimaryNavigationData(): void {
    combineLatest([this.headerNavigationService.primaryLinks$, this.isCompressed$])
      .pipe(
        map(([primaryNavItems, isCompressed]) => (!isCompressed ? primaryNavItems : [])),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(primaryNavItems => {
        this.primaryNavItems = primaryNavItems;
        this.cdr.markForCheck();
      });
  }

  private subscribeMetaNavigationData(): void {
    this.configService.metaNavConfig$.pipe(takeUntil(this.unsubscribe$)).subscribe(([itemsMap, items]) => {
      this.metaNavItemsMap = itemsMap;
      this.metaNavItems = items;
      this.metaNavItemManageMyTools = this.configService.getManageMyToolsLink();
      this.cdr.markForCheck();
    });
  }

  private checkAccountSync({ isAccountSync }: UserAccountData): void {
    if (isAccountSync) {
      const prefix = 'header.account.messageSync.';
      this.toastMessagesService.info(
        this.i18nextPipe.transform(prefix + 'copy'),
        this.i18nextPipe.transform(prefix + 'title')
      );
    }
  }

  /**
   * Track user logged in success event
   * User clicks on login button will set session storage STORAGE_KEY_LOGIN_CLICKED=true
   * Check if such storage exists, if yes then track logged in success event.
   *
   * @param isLoggedIn
   * @private
   */
  private trackUserLoggedIn({ isLoggedIn }: UserAccountData): void {
    if (isLoggedIn && this.storageService.getItem(STORAGE_KEY_LOGIN_CLICKED, StorageType.SESSION, true)) {
      this.storageService.removeItem(STORAGE_KEY_LOGIN_CLICKED, StorageType.SESSION, true);
      this.analyticsService.sendEvent({
        category: 'SiteOperations',
        action: 'Login',
        label: 'Top Menu|Success'
      });
    }
  }

  private handleElementFocusout(newFocusedEle: HTMLElement): void {
    const isChildElement = this.element.contains(newFocusedEle);
    const isPrimaryNavElement = this.primaryNav.nativeElement.contains(newFocusedEle);
    if (!isChildElement || isPrimaryNavElement) {
      this.searchComponent?.restoreInitialState();
    }
  }

  private unsubscribeFocusout(): void {
    this.focusoutListener?.();
    this.focusoutListener = null;
  }
}
